<script lang="ts">
  import {
    hsvToRgb,
    isValidHex,
    normalizeHex,
    parseHex,
    rgbToHex,
    rgbToHsv,
  } from '$lib/util/color';

  interface Props {
    value: string;
    onchange: (hex: string) => void;
    ariaLabel?: string;
  }
  const props: Props = $props();

  let open = $state(false);
  let h = $state(0);
  let s = $state(1);
  let v = $state(1);
  let hexInput = $state('');
  let svArea: HTMLDivElement | undefined = $state();
  let hueBar: HTMLDivElement | undefined = $state();
  let popover: HTMLDivElement | undefined = $state();
  let triggerEl: HTMLButtonElement | undefined = $state();
  let popX = $state(0);
  let popY = $state(0);

  // Keep HSV in sync with incoming `props.value` whenever the popover is
  // closed (so external changes don't fight the picker's local state while
  // open).
  $effect(() => {
    if (!open) {
      const rgb = parseHex(props.value);
      const hsv = rgbToHsv(rgb);
      // Preserve hue when value is achromatic (s=0) so the wheel doesn't
      // jump to red on grey.
      if (hsv.s > 0) h = hsv.h;
      s = hsv.s;
      v = hsv.v;
      hexInput = props.value;
    }
  });

  function commit() {
    const rgb = hsvToRgb({ h, s, v });
    const hex = rgbToHex(rgb);
    hexInput = hex;
    props.onchange(hex);
  }

  function onSvPointer(e: PointerEvent) {
    if (!svArea) return;
    (e.target as Element).setPointerCapture?.(e.pointerId);
    update(e);
    function move(ev: PointerEvent) {
      update(ev);
    }
    function up() {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    }
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);

    function update(ev: PointerEvent) {
      if (!svArea) return;
      const r = svArea.getBoundingClientRect();
      const x = Math.max(0, Math.min(r.width, ev.clientX - r.left));
      const y = Math.max(0, Math.min(r.height, ev.clientY - r.top));
      s = x / r.width;
      v = 1 - y / r.height;
      commit();
    }
  }

  function onHuePointer(e: PointerEvent) {
    if (!hueBar) return;
    (e.target as Element).setPointerCapture?.(e.pointerId);
    update(e);
    function move(ev: PointerEvent) {
      update(ev);
    }
    function up() {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    }
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);

    function update(ev: PointerEvent) {
      if (!hueBar) return;
      const r = hueBar.getBoundingClientRect();
      const x = Math.max(0, Math.min(r.width, ev.clientX - r.left));
      h = (x / r.width) * 360;
      commit();
    }
  }

  function onHexChange(e: Event) {
    const raw = (e.target as HTMLInputElement).value;
    if (!isValidHex(raw)) return;
    const norm = normalizeHex(raw);
    const rgb = parseHex(norm);
    const hsv = rgbToHsv(rgb);
    h = hsv.h;
    s = hsv.s;
    v = hsv.v;
    props.onchange(norm);
  }

  function toggle() {
    open = !open;
    if (open) positionPopover();
  }

  function positionPopover() {
    if (!triggerEl) return;
    const r = triggerEl.getBoundingClientRect();
    popX = r.left;
    popY = r.bottom + 6;
    // Reposition after mount so we can clamp to viewport.
    queueMicrotask(() => {
      if (!popover) return;
      const pr = popover.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      if (popX + pr.width > vw - 8) popX = Math.max(8, vw - pr.width - 8);
      if (popY + pr.height > vh - 8) popY = Math.max(8, r.top - pr.height - 6);
    });
  }

  function onWindowClick(e: MouseEvent) {
    if (!open) return;
    const target = e.target as Node;
    if (popover?.contains(target)) return;
    if (triggerEl?.contains(target)) return;
    open = false;
  }
  function onKey(e: KeyboardEvent) {
    if (e.key === 'Escape') open = false;
  }
</script>

<svelte:window onmousedown={onWindowClick} onkeydown={onKey} />

<button
  bind:this={triggerEl}
  class="swatch"
  style="background: {props.value};"
  onclick={toggle}
  aria-label={props.ariaLabel ?? 'Pick color'}
  aria-haspopup="dialog"
  aria-expanded={open}
  type="button"
></button>

{#if open}
  <div
    class="popover"
    role="dialog"
    bind:this={popover}
    style="left: {popX}px; top: {popY}px;"
  >
    <div
      class="sv-area"
      bind:this={svArea}
      onpointerdown={onSvPointer}
      style="--h: {h};"
      role="slider"
      aria-label="Saturation and brightness"
      aria-valuenow={Math.round(s * 100)}
      tabindex="0"
    >
      <div
        class="sv-handle"
        style="left: {s * 100}%; top: {(1 - v) * 100}%; background: {props.value};"
      ></div>
    </div>
    <div
      class="hue-bar"
      bind:this={hueBar}
      onpointerdown={onHuePointer}
      role="slider"
      aria-label="Hue"
      aria-valuenow={Math.round(h)}
      tabindex="0"
    >
      <div class="hue-handle" style="left: {(h / 360) * 100}%;"></div>
    </div>
    <div class="hex-row">
      <input
        class="hex"
        type="text"
        bind:value={hexInput}
        onchange={onHexChange}
        spellcheck="false"
        maxlength="7"
      />
      <button
        class="close"
        onclick={() => (open = false)}
        aria-label="Close picker"
        type="button">done</button
      >
    </div>
  </div>
{/if}

<style>
  .swatch {
    width: 28px;
    height: 22px;
    border: 1px solid var(--border);
    border-radius: 3px;
    padding: 0;
    cursor: pointer;
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.2);
  }
  .swatch:hover {
    border-color: var(--accent);
  }
  .popover {
    position: fixed;
    z-index: 100;
    background: var(--surface);
    border: 1px solid var(--accent-dim);
    border-radius: 6px;
    padding: 10px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
    width: 220px;
  }
  .sv-area {
    position: relative;
    width: 100%;
    height: 150px;
    border-radius: 3px;
    cursor: crosshair;
    background:
      linear-gradient(to top, #000, transparent),
      linear-gradient(to right, #fff, transparent),
      hsl(var(--h), 100%, 50%);
    touch-action: none;
  }
  .sv-handle {
    position: absolute;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    border: 2px solid #fff;
    transform: translate(-50%, -50%);
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.5);
    pointer-events: none;
  }
  .hue-bar {
    position: relative;
    height: 14px;
    border-radius: 3px;
    background: linear-gradient(
      to right,
      #f00 0%,
      #ff0 17%,
      #0f0 33%,
      #0ff 50%,
      #00f 67%,
      #f0f 83%,
      #f00 100%
    );
    cursor: ew-resize;
    touch-action: none;
  }
  .hue-handle {
    position: absolute;
    top: 50%;
    width: 6px;
    height: 18px;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.6);
    border-radius: 2px;
    transform: translate(-50%, -50%);
    pointer-events: none;
  }
  .hex-row {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 6px;
  }
  .hex {
    font-family: var(--font-mono);
    font-size: 12px;
    text-transform: lowercase;
  }
  .close {
    font-size: 11px;
    padding: 4px 10px;
  }
</style>
